<template>
  <div class="pdv-login">

    <!-- ── Coluna esquerda: Branding ──────────────────────────────────────── -->
    <aside class="pdv-login__branding" aria-hidden="true">
      <div class="pdv-login__branding-content">
        <img
          src="media/brand/logo.png"
          alt="PDV Desktop"
          class="pdv-login__brand-logo"
        />
        <h1 class="pdv-login__brand-title">PDV Desktop</h1>
        <p class="pdv-login__brand-sub">
          Ponto de venda integrado e pronto para operar mesmo offline
        </p>
        <ul class="pdv-login__features">
          <li><i class="fas fa-wifi-slash"></i> Funciona sem internet</li>
          <li><i class="fas fa-file-invoice"></i> Emissão de NFC-e</li>
          <li><i class="fas fa-sync-alt"></i> Sincronização automática</li>
          <li><i class="fas fa-cash-register"></i> Controle de caixa</li>
        </ul>
      </div>
    </aside>

    <!-- ── Coluna direita: Formulário ─────────────────────────────────────── -->
    <main class="pdv-login__panel">

      <!-- Indicador de conexão -->
      <div
        :class="[
          'pdv-login__conn-badge',
          online
            ? 'pdv-login__conn-badge--online'
            : 'pdv-login__conn-badge--offline',
        ]"
        role="status"
        aria-live="polite"
      >
        <span class="pdv-login__conn-dot"></span>
        {{ online ? "Online" : "Offline" }}
      </div>

      <div class="pdv-login__card">

        <!-- Logo (resoluções estreitas) -->
        <img
          src="media/brand/logo.png"
          alt="PDV Desktop"
          class="pdv-login__card-logo"
        />

        <h2 class="pdv-login__card-title">PDV Desktop</h2>
        <p class="pdv-login__card-sub">Acesse para operar o terminal de venda</p>

        <!-- Alerta: offline sem sessão salva ─────────────────────────────── -->
        <div
          v-if="!online && !temCacheOffline"
          class="pdv-login__alert pdv-login__alert--warning"
          role="alert"
        >
          <i class="fas fa-exclamation-triangle"></i>
          Sem conexão. Conecte-se à internet para fazer o primeiro acesso.
        </div>

        <!-- Aviso: offline com sessão salva ──────────────────────────────── -->
        <div
          v-if="!online && temCacheOffline"
          class="pdv-login__alert pdv-login__alert--info"
          role="alert"
        >
          <i class="fas fa-info-circle"></i>
          Você está offline. Informe usuário e senha do último acesso neste terminal.
        </div>

        <!-- Erro de autenticação ─────────────────────────────────────────── -->
        <div
          v-if="erroLogin"
          class="pdv-login__alert pdv-login__alert--error"
          role="alert"
        >
          <i class="fas fa-times-circle"></i>
          {{ erroLogin }}
        </div>

        <!-- Formulário ───────────────────────────────────────────────────── -->
        <form @submit.prevent="entrar" novalidate autocomplete="on">

          <!-- E-mail -->
          <div class="pdv-login__field">
            <label class="pdv-login__label" for="login-email">
              E-mail / Usuário
            </label>
            <input
              id="login-email"
              v-model.trim="form.email"
              type="email"
              class="pdv-login__input"
              :class="{ 'pdv-login__input--error': erros.email }"
              placeholder="seu@email.com"
              autocomplete="username"
              :disabled="carregando || (!online && !temCacheOffline)"
              @input="erros.email = ''"
            />
            <span v-if="erros.email" class="pdv-login__field-error">
              {{ erros.email }}
            </span>
          </div>

          <!-- Senha -->
          <div class="pdv-login__field">
            <label class="pdv-login__label" for="login-senha">Senha</label>
            <div class="pdv-login__input-wrap">
              <input
                id="login-senha"
                v-model="form.senha"
                :type="mostrarSenha ? 'text' : 'password'"
                class="pdv-login__input pdv-login__input--has-icon"
                :class="{ 'pdv-login__input--error': erros.senha }"
                placeholder="••••••••"
                autocomplete="current-password"
                :disabled="carregando || (!online && !temCacheOffline)"
                @input="erros.senha = ''"
              />
              <button
                type="button"
                class="pdv-login__toggle-senha"
                tabindex="-1"
                :disabled="carregando"
                :title="mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'"
                @click="mostrarSenha = !mostrarSenha"
              >
                <i :class="mostrarSenha ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <span v-if="erros.senha" class="pdv-login__field-error">
              {{ erros.senha }}
            </span>
          </div>

          <!-- Botão Entrar -->
          <button
            type="submit"
            class="pdv-login__btn"
            :disabled="carregando || (!online && !temCacheOffline)"
          >
            <span v-if="carregando" class="pdv-login__btn-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span v-if="!online && temCacheOffline && !carregando">
              Continuar Offline
            </span>
            <span v-else-if="!carregando">Entrar</span>
            <span v-else>Entrando...</span>
          </button>

        </form>
      </div>

      <p class="pdv-login__version">{{ brandName }}</p>
    </main>
  </div>
</template>

<script>
import PdvDesktopAuthService from "../services/PdvDesktopAuthService";
import TerminalConfigService from "../services/TerminalConfigService";
import BrandService from "../services/BrandService";

export default {
  name: "PdvDesktopLoginPage",

  data() {
    return {
      brandName: BrandService.productName,
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      temSessaoSalva: false,
      temCacheOffline: false,

      form: { email: "", senha: "" },
      erros: { email: "", senha: "" },

      erroLogin: "",
      carregando: false,
      mostrarSenha: false,
    };
  },

  async created() {
    BrandService.carregar().then(() => { this.brandName = BrandService.productName; });
    this._onOnline  = () => { this.online = true; };
    this._onOffline = () => { this.online = false; };
    window.addEventListener("online",  this._onOnline);
    window.addEventListener("offline", this._onOffline);

    if (this.$isDesktop) {
      const [sessao, email, cache] = await Promise.all([
        TerminalConfigService.temSessaoSalva(),
        TerminalConfigService.getEmailSalvo(),
        PdvDesktopAuthService.temCredencialOffline(),
      ]);
      this.temSessaoSalva = sessao;
      this.temCacheOffline = cache;
      if (email) this.form.email = email;
    }
  },

  beforeDestroy() {
    window.removeEventListener("online",  this._onOnline);
    window.removeEventListener("offline", this._onOffline);
  },

  methods: {
    _validar() {
      let ok = true;
      this.erros = { email: "", senha: "" };

      // Offline ou online, sempre exigimos e-mail e senha — no modo offline a
      // senha é validada contra o cache local (hash), não contra o servidor.
      if (!this.form.email) {
        this.erros.email = "Informe o e-mail ou usuário.";
        ok = false;
      }
      if (!this.form.senha) {
        this.erros.senha = "Informe a senha.";
        ok = false;
      }
      return ok;
    },

    async entrar() {
      this.erroLogin = "";
      if (!this._validar()) return;

      this.carregando = true;
      try {
        if (!this.online) {
          // ── Modo offline: valida credenciais contra o cache local ───────
          if (!this.temCacheOffline) {
            this.erroLogin =
              "É necessário fazer o primeiro login com internet neste terminal.";
            return;
          }
          await PdvDesktopAuthService.loginOffline(this.form.email, this.form.senha);
          await this._redirecionar();
          return;
        }

        // ── Modo online: autentica no servidor ─────────────────────────
        await PdvDesktopAuthService.login(this.form.email, this.form.senha);
        await this._redirecionar();

      } catch (e) {
        this.erroLogin =
          e.message || "Não foi possível autenticar. Tente novamente.";
      } finally {
        this.carregando = false;
      }
    },

    /**
     * Após autenticação bem-sucedida, verifica se o terminal já está
     * configurado e redireciona para a tela correta.
     */
    async _redirecionar() {
      const { configurado } = await TerminalConfigService.verificarConfiguracaoLocal();
      if (configurado) {
        if (this.online) {
          try {
            await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
          } catch (e) {
            if (TerminalConfigService.isCodigoBloqueioEmpresa(e?.code)) {
              this.$router.push({ name: "pdvDesktopBloqueio" });
              return;
            }
            this.$router.push({ name: "pdvDesktopConfiguracaoTerminal" });
            return;
          }
        }
        this.$router.push({ name: "pdvDesktopDashboard" });
      } else {
        this.$router.push({ name: "pdvDesktopConfiguracaoTerminal" });
      }
    },
  },
};
</script>

<style scoped>
/* ── Layout geral ──────────────────────────────────────────────────────── */
.pdv-login {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #f5f7fa;
}

/* ── Coluna esquerda (branding) ────────────────────────────────────────── */
.pdv-login__branding {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(140deg, #172c30 0%, var(--primary-color, #ee7361) 100%);
  padding: 2rem;
  color: #fff;
}

.pdv-login__branding-content {
  max-width: 380px;
  text-align: center;
}

.pdv-login__brand-logo {
  max-height: 52px;
  filter: brightness(0) invert(1);
  margin-bottom: 1.5rem;
}

.pdv-login__brand-title {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 0 0 0.5rem;
  color: #fff;
}

.pdv-login__brand-sub {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 2rem;
  line-height: 1.5;
}

.pdv-login__features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  display: inline-block;
}

.pdv-login__features li {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  padding: 0.35rem 0;
}

.pdv-login__features li i {
  width: 18px;
  text-align: center;
  opacity: 0.85;
}

/* ── Coluna direita (painel de formulário) ─────────────────────────────── */
.pdv-login__panel {
  width: 420px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  position: relative;
  background: #f5f7fa;
}

/* ── Badge de conexão ──────────────────────────────────────────────────── */
.pdv-login__conn-badge {
  position: absolute;
  top: 1.25rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  letter-spacing: 0.3px;
}

.pdv-login__conn-badge--online {
  background: #e6f4ea;
  color: #1e7e34;
}

.pdv-login__conn-badge--offline {
  background: #fff3cd;
  color: #856404;
}

.pdv-login__conn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.pdv-login__conn-badge--online .pdv-login__conn-dot {
  background: #28a745;
}

.pdv-login__conn-badge--offline .pdv-login__conn-dot {
  background: #ffc107;
}

/* ── Card do formulário ────────────────────────────────────────────────── */
.pdv-login__card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 380px;
}

.pdv-login__card-logo {
  display: none;
  max-height: 36px;
  margin-bottom: 1rem;
}

.pdv-login__card-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.25rem;
  letter-spacing: -0.3px;
}

.pdv-login__card-sub {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0 0 1.5rem;
}

/* ── Alertas ───────────────────────────────────────────────────────────── */
.pdv-login__alert {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  line-height: 1.45;
}

.pdv-login__alert i {
  margin-top: 1px;
  flex-shrink: 0;
}

.pdv-login__alert--warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffe083;
}

.pdv-login__alert--info {
  background: #e8f4fd;
  color: #0c5460;
  border: 1px solid #b8daff;
}

.pdv-login__alert--error {
  background: #fdecea;
  color: #842029;
  border: 1px solid #f5c2c7;
}

/* ── Campos de formulário ──────────────────────────────────────────────── */
.pdv-login__field {
  margin-bottom: 1.1rem;
}

.pdv-login__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}

.pdv-login__input-wrap {
  position: relative;
}

.pdv-login__input {
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.18s, box-shadow 0.18s;
  box-sizing: border-box;
  outline: none;
}

.pdv-login__input--has-icon {
  padding-right: 2.5rem;
}

.pdv-login__input:focus {
  border-color: var(--primary-color, #ee7361);
  box-shadow: 0 0 0 3px rgba(238, 115, 97, 0.15);
}

.pdv-login__input--error {
  border-color: #dc3545;
}

.pdv-login__input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.pdv-login__toggle-senha {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: 0.9rem;
}

.pdv-login__toggle-senha:hover {
  color: #374151;
}

.pdv-login__field-error {
  display: block;
  font-size: 0.78rem;
  color: #dc3545;
  margin-top: 0.3rem;
}

/* ── Botão Entrar ──────────────────────────────────────────────────────── */
.pdv-login__btn {
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, var(--primary-color, #ee7361) 0%, #e05a47 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.18s, transform 0.12s;
  margin-top: 0.5rem;
  letter-spacing: 0.2px;
}

.pdv-login__btn:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.pdv-login__btn:active:not(:disabled) {
  transform: translateY(0);
}

.pdv-login__btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  opacity: 1;
}

/* ── Versão ────────────────────────────────────────────────────────────── */
.pdv-login__version {
  position: absolute;
  bottom: 1rem;
  font-size: 0.72rem;
  color: #9ca3af;
  margin: 0;
}

/* ── Responsivo (tablet/estreito) ─────────────────────────────────────── */
@media (max-width: 768px) {
  .pdv-login__branding {
    display: none;
  }

  .pdv-login__panel {
    width: 100%;
  }

  .pdv-login__card-logo {
    display: block;
  }
}
</style>
